import { useEffect, useState } from 'react';
import { patientPortal } from '@/api/patientPortal';
import { Button } from '@/components/ui/button';
import PatientPortalPageHero from './PatientPortalPageHero';
import PatientPortalCard from './PatientPortalCard';

export default function PatientPortalLoyalty() {
  const [data, setData] = useState<{
    pointsBalance: number;
    ownPointsBalance?: number;
    rupeeValue: number;
    referralCode?: string;
    referralLink?: string;
    promoBalance?: number;
    cashbackBalance?: number;
    membership?: { displayName: string } | null;
    familyMembers?: Array<{ id: string; name: string }>;
    familyPoolingEnabled?: boolean;
    pointExpiryDays?: number;
    appOtpLoginRequired?: boolean;
    activePointLots?: Array<{
      points: number;
      expiresAt: string;
      description: string | null;
    }>;
    recentTransactions?: Array<{
      type: string;
      points: number;
      description: string | null;
      expiresAt?: string | null;
    }>;
    referralEarnings?: {
      totalReferred: number;
      rewardedCount: number;
      referrals: Array<{ referredName: string; status: string }>;
    };
  } | null>(null);
  const [headId, setHeadId] = useState('');

  useEffect(() => {
    patientPortal.getLoyalty().then(setData);
  }, []);

  return (
    <div className="space-y-6">
      <PatientPortalPageHero
        imageKey="loyalty"
        eyebrow="Rewards"
        title="Points dashboard"
        subtitle="Track points, membership tier, and family pooling"
        compact
      />
      <PatientPortalCard className="space-y-4">
      {data?.membership && (
        <span className="inline-block text-sm bg-teal-100 text-teal-800 px-2 py-1 rounded">
          {data.membership.displayName} member
        </span>
      )}
      <p className="text-3xl font-bold text-teal-800">
        {data?.pointsBalance ?? 0} points
        {data?.familyPoolingEnabled ? ' (family pool)' : ''}
      </p>
      {data?.familyPoolingEnabled && data.ownPointsBalance != null && (
        <p className="text-sm text-muted-foreground">Your wallet: {data.ownPointsBalance} points</p>
      )}
      <p className="text-muted-foreground">≈ ₹{data?.rupeeValue?.toFixed(2) ?? '0.00'} redemption value</p>
      <p className="text-sm text-muted-foreground">
        {data?.appOtpLoginRequired
          ? `Sign in with WhatsApp OTP once to earn points. Points expire ${data?.pointExpiryDays ?? 180} days after earn.`
          : `Points expire ${data?.pointExpiryDays ?? 180} days after they are earned.`}
      </p>
      {(data?.activePointLots?.length ?? 0) > 0 && (
        <div className="border rounded-lg p-4 bg-white space-y-2">
          <h2 className="font-semibold">Points & expiry</h2>
          <ul className="text-sm divide-y">
            {data!.activePointLots!.map((lot, i) => (
              <li key={i} className="flex justify-between py-2 gap-3">
                <span>
                  {lot.description ?? 'Points earned'}
                  <span className="block text-xs text-muted-foreground">
                    Expires {new Date(lot.expiresAt).toLocaleDateString('en-IN')}
                  </span>
                </span>
                <span className="text-teal-700 font-medium">+{lot.points}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data?.referralCode && (
        <div className="text-sm space-y-1">
          <p>
            Referral code: <span className="font-mono font-semibold text-teal-800">{data.referralCode}</span>
          </p>
          {data.referralLink && (
            <p className="text-xs text-muted-foreground break-all">Share link: {data.referralLink}</p>
          )}
        </div>
      )}
      {data?.referralEarnings && data.referralEarnings.totalReferred > 0 && (
        <div className="border rounded-lg p-4 bg-white">
          <h2 className="font-semibold mb-2">Referral earnings</h2>
          <p className="text-sm text-muted-foreground">
            {data.referralEarnings.rewardedCount} rewarded of {data.referralEarnings.totalReferred} referrals
          </p>
          <ul className="text-sm mt-2 space-y-1">
            {data.referralEarnings.referrals.slice(0, 10).map((r, i) => (
              <li key={i}>
                {r.referredName} — <span className="text-muted-foreground">{r.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {((data?.promoBalance ?? 0) > 0 || (data?.cashbackBalance ?? 0) > 0) && (
        <p className="text-sm text-muted-foreground">
          Wallet: promo ₹{data?.promoBalance?.toFixed(2)} · cashback ₹{data?.cashbackBalance?.toFixed(2)}
        </p>
      )}

      {data?.familyPoolingEnabled && data.familyMembers && data.familyMembers.length > 1 && (
        <div className="border rounded-lg p-4 bg-white space-y-3">
          <h2 className="font-semibold">Family pooling</h2>
          <p className="text-sm text-muted-foreground">Linked members: {data.familyMembers.map((m) => m.name).join(', ')}</p>
          <select
            className="border rounded px-3 py-2 w-full"
            value={headId}
            onChange={(e) => setHeadId(e.target.value)}
          >
            <option value="">No pool head (earn on own wallet)</option>
            {data.familyMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={() =>
              patientPortal.setPoolHead(headId || null).then(() => patientPortal.getLoyalty().then(setData))
            }
          >
            Save pool head
          </Button>
        </div>
      )}

      <div>
        <h2 className="font-semibold mb-2">Activity</h2>
        <ul className="text-sm space-y-2 border rounded-lg divide-y bg-white">
          {(data?.recentTransactions ?? []).map((tx, i) => (
            <li key={i} className="flex justify-between p-3 gap-3">
              <span>
                {tx.description || tx.type}
                {tx.type === 'EARN' && tx.expiresAt && (
                  <span className="block text-xs text-muted-foreground">
                    Expires {new Date(tx.expiresAt).toLocaleDateString('en-IN')}
                  </span>
                )}
              </span>
              <span className={tx.type === 'EARN' ? 'text-teal-700' : 'text-orange-700'}>
                {tx.type === 'EARN' ? '+' : '-'}
                {tx.points}
              </span>
            </li>
          ))}
        </ul>
      </div>
      </PatientPortalCard>
    </div>
  );
}
